import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import logoImg from '@assets/images/logo.png';
import { Button } from '@shared/widgets/button';
import LogoMobileIcon from '@assets/images/insignia_logo.svg';
import Profile from '@assets/images/user-profile-rounded.svg';
import ProfileMobile from '@assets/images/profile_ico.svg';
import * as FiIcons from 'react-icons/fi';
import * as AiIcons from 'react-icons/ai';
import { useLettersCountStore, useLoginStore, useVerifyCodeStore } from '@lib/store';
import { cn, LocalStorageUtils, RoutePaths } from '@shared/utils';
import { useMediaQuery, useRouteHook } from '@shared/hooks';
import TermsCondiotionsIcon from '@assets/images/Insignia_Annual_Report.svg';
import TariffPlanIcon from '@assets/images/letters-icon.svg';
import ProductsIcon from '@assets/images/menu/menu_products.svg';
import EligibilityIcon from '@assets/images/menu/menu_letters.svg';
import TariffsIcon from '@assets/images/menu/menu_tariffs.svg';
import TermsIcon from '@assets/images/menu/menu_terms.svg';
import CardsIcon from '@assets/images/menu/menu_cards.svg';
import LettersIcon from '@assets/images/menu/menu_letters.svg';
import IcaIcon from '@assets/images/menu/menu_ica.svg';
import ContactsIcon from '@assets/images/menu/menu_contacts.svg';
import SettingsIcon from '@assets/images/menu/menu_settings.svg';
import LogoutIcon from '@assets/images/menu/menu_logout.svg';

import styles from './header.module.css';

const links = [
  {
    id: 1,
    header: 'Insignia Products',
    link: RoutePaths.products.onNavigate(),
    icon: <TermsCondiotionsIcon className='mr-4 max-md:hidden' />,
    iconMobile: <ProductsIcon className='mr-4 hidden max-md:inline-block' />,
  },
  {
    id: 2,
    header: 'Eligibility Form',
    link: RoutePaths.form.onNavigate(),
    icon: <TermsCondiotionsIcon className='mr-4 max-md:hidden' />,
    iconMobile: <EligibilityIcon className='mr-4 hidden max-md:inline-block' />,
  },
  {
    id: 3,
    header: 'Declaration of disputed charges',
    link: RoutePaths.disputedCharges.onNavigate(),
    icon: <TermsCondiotionsIcon className='mr-4 max-md:hidden' />,
    iconMobile: <EligibilityIcon className='mr-4 hidden max-md:inline-block' />,
  },
  {
    id: 4,
    header: 'Schedule of Fees and Charges',
    link: RoutePaths.tariff.onNavigate(),
    icon: <TariffPlanIcon className='mr-4 max-md:hidden' />,
    iconMobile: <TariffsIcon className='mr-4 hidden max-md:inline-block' />,
  },
  {
    id: 5,
    header: 'Membership Terms & Conditions',
    link: RoutePaths.conditionsCard.onNavigate(),
    icon: <TermsCondiotionsIcon className='mr-4 max-md:hidden' />,
    iconMobile: <TermsIcon className='mr-4 hidden max-md:inline-block' />,
  },
];

interface IHeaderProps {
  isWhite?: boolean;
}

export const Header = (props: IHeaderProps) => {
  const loginStore = useLoginStore();
  const verifyCodeStore = useVerifyCodeStore();
  const lettersCountStore = useLettersCountStore();

  const [menu, setMenu] = useState(false);
  const [profileMenu, setProfileMenu] = useState(false);

  const stuffData = LocalStorageUtils.getStuff();

  const isMaxMd = useMediaQuery('(max-width: 768px)');
  const { navigate } = useRouteHook();

  const showMenu = () => setMenu(!menu);
  const showProfileMenu = () => setProfileMenu(!profileMenu);

  useEffect(() => {
    lettersCountStore.getLettersCount();
  }, []);

  const logout = () => {
    verifyCodeStore.clear();
    loginStore.clear();
    LocalStorageUtils.removeStuff();
    navigate(RoutePaths.login.onNavigate());
  };

  return (
    <header
      className={cn(
        'flex h-[80px] items-center justify-between bg-white pr-5.5 pl-8.5',
        'max-md:absolute max-md:top-0 max-md:right-0 max-md:left-0 max-md:z-1 max-md:block max-md:h-[50px] max-md:px-3',
        props.isWhite ? 'max-md:bg-white' : 'max-md:bg-[#0f0f114d]',
      )}
    >
      <div className='flex h-[50px] w-[50px] items-center max-md:absolute max-md:left-[50%] max-md:ml-[-25px]'>
        <NavLink to={isMaxMd ? RoutePaths.cards.onNavigate() : RoutePaths.home.onNavigate()}>
          <img className='object-contain max-md:hidden' src={logoImg} alt='logo' />
          <LogoMobileIcon className={cn('max-md:inherit hidden', props.isWhite ? styles.mobileLogo : styles.mobileLogoWhite)} />
        </NavLink>
      </div>
      {stuffData?.token ? (
        <>
          <div className='ml-[55px] flex h-full min-w-[400px] justify-between max-md:hidden'>
            <NavLink
              to={RoutePaths.cards.onNavigate()}
              className={({ isActive }) =>
                cn(
                  'relative flex h-full min-w-[55px] items-center justify-center px-4 text-[22px] font-light text-[#272727] hover:opacity-50 hover:after:absolute hover:after:right-0 hover:after:bottom-0 hover:after:left-0 hover:after:mx-auto hover:after:h-[4px] hover:after:w-[55px] hover:after:bg-[#000000]',
                  isActive &&
                    'after:absolute after:right-0 after:bottom-0 after:left-0 after:mx-auto after:h-[4px] after:w-[55px] after:bg-[#000000] max-md:after:top-0 max-md:after:right-0 max-md:after:bottom-0 max-md:after:left-auto max-md:after:h-auto max-md:after:w-[3px] max-md:after:bg-[#d7c29a]',
                )
              }
            >
              Cards
            </NavLink>
            <NavLink
              to={RoutePaths.letters.onNavigate()}
              className={({ isActive }) =>
                cn(
                  'relative flex h-full min-w-[55px] items-center justify-center px-4 text-[22px] font-light text-[#272727] hover:opacity-50 hover:after:absolute hover:after:right-0 hover:after:bottom-0 hover:after:left-0 hover:after:mx-auto hover:after:h-[4px] hover:after:w-[55px] hover:after:bg-[#000000]',
                  isActive &&
                    'after:absolute after:right-0 after:bottom-0 after:left-0 after:mx-auto after:h-[4px] after:w-[55px] after:bg-[#000000] max-md:after:top-0 max-md:after:right-0 max-md:after:bottom-0 max-md:after:left-auto max-md:after:h-auto max-md:after:w-[3px] max-md:after:bg-[#d7c29a]',
                )
              }
            >
              Letters
              {lettersCountStore.lettersCount ? (
                <span className='relative ml-1 flex items-center justify-center rounded-sm bg-[#d0021b] px-1 text-xs text-[#fff]'>
                  {lettersCountStore.lettersCount}
                </span>
              ) : null}
            </NavLink>
            <NavLink
              to={RoutePaths.contact.onNavigate()}
              className={({ isActive }) =>
                cn(
                  'relative flex h-full min-w-[55px] items-center justify-center px-4 text-[22px] font-light text-[#272727] hover:opacity-50 hover:after:absolute hover:after:right-0 hover:after:bottom-0 hover:after:left-0 hover:after:mx-auto hover:after:h-[4px] hover:after:w-[55px] hover:after:bg-[#000000]',
                  isActive &&
                    'after:absolute after:right-0 after:bottom-0 after:left-0 after:mx-auto after:h-[4px] after:w-[55px] after:bg-[#000000] max-md:after:top-0 max-md:after:right-0 max-md:after:bottom-0 max-md:after:left-auto max-md:after:h-auto max-md:after:w-[3px] max-md:after:bg-[#d7c29a]',
                )
              }
            >
              Contact
            </NavLink>
            <a
              className='relative flex h-full min-w-[55px] items-center justify-center px-4 text-[22px] font-light text-[#272727] hover:opacity-50 hover:after:absolute hover:after:right-0 hover:after:bottom-0 hover:after:left-0 hover:after:mx-auto hover:after:h-[4px] hover:after:w-[55px] hover:after:bg-[#000000]'
              href='https://ica-online.com'
              target='_blank'
              rel='noreferrer'
            >
              ICA
            </a>
            <a
              className='relative flex h-full min-w-[55px] items-center justify-center px-4 text-[22px] font-light text-[#272727] hover:opacity-50 hover:after:absolute hover:after:right-0 hover:after:bottom-0 hover:after:left-0 hover:after:mx-auto hover:after:h-[4px] hover:after:w-[55px] hover:after:bg-[#000000]'
              href='https://rewards.insignia.com/'
              target='_blank'
              rel='noreferrer'
            >
              Rewards
            </a>
          </div>

          <div className='flex h-full items-center max-md:justify-between'>
            <button onClick={showProfileMenu} className='hidden border-none bg-none max-md:block'>
              <Profile width={27} height={27} className='max-md:hidden' />
              <ProfileMobile
                width={27}
                height={27}
                className={cn('max-md:inherit hidden', props.isWhite ? styles.profileOpenerButton : styles.profileOpenerButtonWhite)}
              />
            </button>
            <button onClick={showMenu} className='cursor-pointer border-none bg-none outline-none max-md:before:-rotate-45 max-md:after:rotate-45'>
              <FiIcons.FiMenu className={cn('text-3xl', props.isWhite ? 'max-md:stroke-black' : 'max-md:stroke-white')} />
            </button>
            <NavLink
              to={RoutePaths.profile.onNavigate()}
              className={({ isActive }) =>
                cn(
                  'relative mr-4 flex h-full w-[55px] min-w-[55px] items-center justify-center px-4 text-[22px] font-light text-[#272727] hover:opacity-50 hover:after:absolute hover:after:right-0 hover:after:bottom-0 hover:after:left-0 hover:after:mx-auto hover:after:h-[4px] hover:after:w-[55px] hover:after:bg-[#000000] max-md:hidden',
                  isActive &&
                    'after:absolute after:right-0 after:bottom-0 after:left-0 after:mx-auto after:h-[4px] after:w-[55px] after:bg-[#000000] max-md:after:top-0 max-md:after:right-0 max-md:after:bottom-0 max-md:after:left-auto max-md:after:h-auto max-md:after:w-[3px] max-md:after:bg-[#d7c29a]',
                )
              }
            >
              <div className='relative flex h-full items-center'>
                <Profile width={27} height={27} />
              </div>
            </NavLink>
            <Button className='w-[90px] !p-0 max-md:hidden' onClick={logout}>
              {stuffData.token ? 'Log Out' : 'Log In'}
            </Button>
          </div>
          <div className={cn(styles['side_menu'], styles['side_menu_profile'], profileMenu && styles['active'])}>
            <button
              onClick={showProfileMenu}
              className='justify-right ml-auto flex cursor-pointer border-none bg-none px-15 py-5 outline-none max-md:relative max-md:mt-[9px] max-md:mr-[10px] max-md:mb-[-50px] max-md:ml-auto max-md:block max-md:h-[40px] max-md:w-[40px] max-md:p-0 max-md:before:absolute max-md:before:top-[50%] max-md:before:left-[50%] max-md:before:ml-[-11px] max-md:before:w-[22px] max-md:before:rotate-[-45deg] max-md:before:border-b max-md:before:border-b-[#000] max-md:after:absolute max-md:after:top-[50%] max-md:after:left-[50%] max-md:after:ml-[-11px] max-md:after:w-[22px] max-md:after:rotate-[45deg] max-md:after:border-b max-md:after:border-b-[#000]'
            >
              <AiIcons.AiOutlineClose className={cn('text-3xl max-md:hidden', props.isWhite ? 'max-md:stroke-[#000]' : 'max-md:stroke-[#fff]')} />
            </button>
            <div className='mb-4 hidden h-[62px] w-full border-b border-b-[#4c4c4c1a] pl-4 font-[ProximaNova-Medium] text-[22px] leading-[62px] font-medium text-[#4c4c4c] max-md:block'>
              Profile
            </div>
            <div className='min-w:-[1px] hidden h-auto max-md:block'>
              <NavLink
                to={RoutePaths.profile.onNavigate()}
                className={({ isActive }) =>
                  cn(
                    'relative flex h-[52px] w-full justify-start py-4 pl-4',
                    isActive &&
                      'after:absolute after:right-0 after:bottom-0 after:left-0 after:mx-auto after:h-[4px] after:w-[55px] after:bg-[#000000] max-md:bg-[#d7c29a20] max-md:font-bold max-md:after:top-0 max-md:after:right-0 max-md:after:bottom-0 max-md:after:left-auto max-md:after:h-auto max-md:after:w-[3px] max-md:after:bg-[#d7c29a]',
                  )
                }
              >
                <SettingsIcon className='mr-4' />
                Settings
              </NavLink>
              <NavLink
                to={RoutePaths.login.onNavigate()}
                className='relative flex h-[52px] w-full justify-start py-4 pl-4 text-[#e54338]'
                onClick={logout}
              >
                <LogoutIcon className='mr-4' />
                Logout
              </NavLink>
            </div>
          </div>
          <div className={cn(styles['side_menu'], menu && styles['active'])}>
            <button
              onClick={showMenu}
              className='justify-right ml-auto flex cursor-pointer border-none bg-none px-15 py-5 outline-none max-md:relative max-md:mt-[9px] max-md:mr-[10px] max-md:mb-[-50px] max-md:ml-auto max-md:block max-md:h-[40px] max-md:w-[40px] max-md:p-0 max-md:before:absolute max-md:before:top-[50%] max-md:before:left-[50%] max-md:before:ml-[-11px] max-md:before:w-[22px] max-md:before:rotate-[-45deg] max-md:before:border-b max-md:before:border-b-[#000] max-md:after:absolute max-md:after:top-[50%] max-md:after:left-[50%] max-md:after:ml-[-11px] max-md:after:w-[22px] max-md:after:rotate-[45deg] max-md:after:border-b max-md:after:border-b-[#000]'
            >
              <AiIcons.AiOutlineClose className={cn('text-3xl max-md:hidden', props.isWhite ? 'max-md:stroke-black' : 'max-md:stroke-white')} />
            </button>
            <div className='mb-4 hidden h-[62px] w-full border-b border-b-[#4c4c4c1a] pl-4 font-[ProximaNova-Medium] text-[22px] leading-[62px] font-medium text-[#4c4c4c] max-md:block'>
              Menu
            </div>
            <div className='min-w:-[1px] mb-4 hidden h-auto border-b border-b-[#4c4c4c1a] pb-4 max-md:block'>
              <NavLink
                to={RoutePaths.cards.onNavigate()}
                className={({ isActive }) =>
                  cn(
                    'relative flex h-[52px] w-full justify-start py-4 pl-4',
                    isActive &&
                      'after:absolute after:right-0 after:bottom-0 after:left-0 after:mx-auto after:h-[4px] after:w-[55px] after:bg-[#000000] max-md:bg-[#d7c29a20] max-md:font-bold max-md:after:top-0 max-md:after:right-0 max-md:after:bottom-0 max-md:after:left-auto max-md:after:h-auto max-md:after:w-[3px] max-md:after:bg-[#d7c29a]',
                  )
                }
              >
                <CardsIcon className='mr-4' />
                Cards
              </NavLink>
              <NavLink
                to={RoutePaths.letters.onNavigate()}
                className={({ isActive }) =>
                  cn(
                    'relative flex h-[52px] w-full justify-start py-4 pl-4',
                    isActive &&
                      'after:absolute after:right-0 after:bottom-0 after:left-0 after:mx-auto after:h-[4px] after:w-[55px] after:bg-[#000000] max-md:bg-[#d7c29a20] max-md:font-bold max-md:after:top-0 max-md:after:right-0 max-md:after:bottom-0 max-md:after:left-auto max-md:after:h-auto max-md:after:w-[3px] max-md:after:bg-[#d7c29a]',
                  )
                }
              >
                <LettersIcon className='mr-4' />
                Letters
                {lettersCountStore.lettersCount ? (
                  <span className='relative ml-1 flex items-center justify-center rounded-sm bg-[#d0021b] px-1 text-xs text-[#fff]'>
                    {lettersCountStore.lettersCount}
                  </span>
                ) : null}
              </NavLink>
              <NavLink
                to={RoutePaths.contact.onNavigate()}
                className={({ isActive }) =>
                  cn(
                    'relative flex h-[52px] w-full justify-start py-4 pl-4',
                    isActive &&
                      'after:absolute after:right-0 after:bottom-0 after:left-0 after:mx-auto after:h-[4px] after:w-[55px] after:bg-[#000000] max-md:bg-[#d7c29a20] max-md:font-bold max-md:after:top-0 max-md:after:right-0 max-md:after:bottom-0 max-md:after:left-auto max-md:after:h-auto max-md:after:w-[3px] max-md:after:bg-[#d7c29a]',
                  )
                }
              >
                <ContactsIcon className='mr-4' />
                Contact
              </NavLink>
              <a href='https://ica-online.com' className='relative flex h-[52px] w-full justify-start py-4 pl-4' target='_blank' rel='noreferrer'>
                <IcaIcon className='mr-4' />
                ICA
              </a>
              <a
                href='https://rewards.insignia.com'
                className='relative flex h-[52px] w-full justify-start py-4 pl-4'
                target='_blank'
                rel='noreferrer'
              >
                <IcaIcon className='mr-4' />
                Rewards
              </a>
            </div>
            <ul className='my-4 flex flex-col px-5 max-md:m-0 max-md:p-0'>
              {links.map((item) => (
                <li
                  key={item.id}
                  className='my-2 pb-1 duration-100 after:mt-[10px] after:ml-auto after:block after:h-[2px] after:w-0 after:bg-[#000] after:transition-all after:duration-300 hover:after:w-[80%] hover:after:transition-all hover:after:duration-300 max-md:m-0 max-md:h-auto max-md:p-0 max-md:after:hidden'
                >
                  <NavLink
                    to={item.link}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center justify-start font-light text-[#000] max-md:relative max-md:flex max-md:h-[52px] max-md:w-full max-md:justify-start max-md:px-4 max-md:py-4 max-md:pr-0',
                        isActive &&
                          'after:absolute after:right-0 after:bottom-0 after:left-0 after:mx-auto after:h-[4px] after:w-[55px] after:bg-[#000000] max-md:bg-[#d7c29a20] max-md:font-bold max-md:after:top-0 max-md:after:right-0 max-md:after:bottom-0 max-md:after:left-auto max-md:after:h-auto max-md:after:w-[3px] max-md:after:bg-[#d7c29a]',
                      )
                    }
                  >
                    {item.icon}
                    {item.iconMobile}
                    <span>{item.header}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        </>
      ) : null}
    </header>
  );
};
